import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useBrand, BRAND_META } from "../context/BrandContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import {
    SignOut,
    Check,
    UsersThree,
    CaretRight,
    Tag,
    ArrowsClockwise,
    ClockCounterClockwise,
    Plus,
    Trash,
    ArrowUp,
    ArrowDown,
    PencilSimple,
} from "@phosphor-icons/react";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";

export default function Settings() {
    const { user, logout } = useAuth();
    const { settings, save, refresh } = useSettings();
    const { current: brandId, brands } = useBrand();
    const navigate = useNavigate();
    const isOwner = user?.role === "owner";
    const isEditor = isOwner || user?.role === "admin_input" || user?.role === "admin";

    const [local, setLocal] = useState(settings);
    useEffect(() => setLocal(settings), [settings]);

    const [savingCatalog, setSavingCatalog] = useState(false);
    const [savingDisplay, setSavingDisplay] = useState(false);

    const [cp, setCp] = useState({ current_password: "", new_password: "", confirm: "" });
    const [cpBusy, setCpBusy] = useState(false);
    const [cpErr, setCpErr] = useState("");

    // Independent collapse state per section — all closed on each page visit (not persisted).
    const [openAccount, setOpenAccount] = useState(false);
    const [openCatalogSettings, setOpenCatalogSettings] = useState(false);

    const saveSection = async (patch, setBusy) => {
        setBusy(true);
        try {
            await save(patch);
            toast.success("Settings saved");
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        setCpErr("");
        if (cp.new_password !== cp.confirm) {
            setCpErr("New password and confirmation do not match");
            return;
        }
        setCpBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: cp.current_password,
                new_password: cp.new_password,
            });
            toast.success("Password changed");
            setCp({ current_password: "", new_password: "", confirm: "" });
        } catch (ex) {
            setCpErr(formatApiError(ex?.response?.data?.detail) || "Failed to change password");
        } finally {
            setCpBusy(false);
        }
    };

    const onLogout = async () => {
        await logout();
        navigate("/login");
    };

    const currentBrandName = BRAND_META[brandId]?.name || brandId;

    return (
        <div className="animate-fade-up max-w-3xl w-full font-sans">
            <div className="eyebrow">Settings</div>
            <h1 className="font-serif text-4xl md:text-5xl mt-1 tracking-tight">Preferences</h1>

            <section className="mt-6 border border-mist rounded-sm bg-white p-6" data-testid="section-account">
                <AccordionHeader title="Account" open={openAccount} onToggle={() => setOpenAccount((o) => !o)} testId="section-account-toggle" />
                <div className={openAccount ? "" : "hidden"} data-testid="section-account-body">
                <dl className="grid grid-cols-2 gap-4 mt-4 text-sm">
                    <div>
                        <dt className="eyebrow">Name</dt>
                        <dd className="mt-1">{user?.name}</dd>
                    </div>
                    <div>
                        <dt className="eyebrow">Email</dt>
                        <dd className="mt-1 font-mono break-all">{user?.email}</dd>
                    </div>
                    <div>
                        <dt className="eyebrow">Role</dt>
                        <dd className="mt-1 uppercase tracking-widest">{user?.role}</dd>
                    </div>
                </dl>

                <form onSubmit={submitPassword} className="mt-6 border-t border-mist pt-5 space-y-3" data-testid="change-password-form">
                    <h3 className="font-serif text-lg">Change Password</h3>
                    <PasswordField label="Current password" testId="pw-current" value={cp.current_password} onChange={(v) => setCp((s) => ({ ...s, current_password: v }))} />
                    <PasswordField label="New password" testId="pw-new" value={cp.new_password} onChange={(v) => setCp((s) => ({ ...s, new_password: v }))} />
                    <PasswordField label="Confirm new password" testId="pw-confirm" value={cp.confirm} onChange={(v) => setCp((s) => ({ ...s, confirm: v }))} />
                    {cpErr && <div className="text-sm text-red-600" data-testid="pw-error">{cpErr}</div>}
                    <div className="flex justify-end">
                        <button type="submit" data-testid="change-password-submit" disabled={cpBusy} className="bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                            {cpBusy ? "Saving…" : "Update password"}
                        </button>
                    </div>
                </form>

                <div className="mt-6 border-t border-mist pt-5 flex justify-end">
                    <button type="button" onClick={onLogout} data-testid="settings-logout" className="inline-flex items-center gap-1 border border-mist px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:border-red-600 hover:text-red-600">
                        <SignOut size={14} weight="light" /> Log out
                    </button>
                </div>
                </div>
            </section>

            {isEditor && (
                <section className="mt-6 border border-mist rounded-sm bg-white p-6" data-testid="section-catalog-settings">
                    <AccordionHeader title="Catalog Settings" open={openCatalogSettings} onToggle={() => setOpenCatalogSettings((o) => !o)} testId="section-catalog-settings-toggle" />
                    <div className={openCatalogSettings ? "" : "hidden"} data-testid="section-catalog-settings-body">
                    <div className="mt-5 space-y-5">
                        <ToggleRow testId="setting-hide-oos" title="Hide Out-of-Stock Products" description="Hide products with zero stock from the default catalog." checked={local.hide_out_of_stock} onChange={(v) => setLocal((s) => ({ ...s, hide_out_of_stock: v }))} />
                        <div>
                            <label className="eyebrow" htmlFor="low-threshold">Low Stock Threshold</label>
                            <input id="low-threshold" type="number" min="0" value={local.low_stock_threshold} onChange={(e) => setLocal((s) => ({ ...s, low_stock_threshold: Number(e.target.value) || 0 }))} data-testid="setting-low-threshold" className="mt-1 w-28 border border-mist rounded-sm px-3 py-2 bg-white text-sm focus:outline-none focus:border-sage-dark" />
                            <p className="text-xs text-neutral-500 mt-2">
                                stock 0 = Out of Stock · stock 1–{local.low_stock_threshold} = Low Stock · stock {local.low_stock_threshold + 1}+ = no badge
                            </p>
                        </div>
                        <ToggleRow testId="setting-show-sp" title="Show Selling Points" description="Display the Selling Points section on product detail." checked={local.show_selling_points} onChange={(v) => setLocal((s) => ({ ...s, show_selling_points: v }))} />
                        <div className="flex justify-end">
                            <button
                                type="button"
                                data-testid="save-catalog-settings"
                                disabled={savingCatalog || savingDisplay}
                                onClick={() =>
                                    saveSection(
                                        {
                                            hide_out_of_stock: local.hide_out_of_stock,
                                            low_stock_threshold: local.low_stock_threshold,
                                            show_selling_points: local.show_selling_points,
                                        },
                                        setSavingCatalog,
                                    ).then(refresh)
                                }
                                className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60"
                            >
                                <Check size={14} weight="bold" /> {savingCatalog ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </div>
                    </div>
                </section>
            )}

            {isEditor && (
                <CategoryManagement brandId={brandId} brandName={currentBrandName} />
            )}

            {isEditor && brandId === "heelsandtea" && (
                <CatalogStructureManagement brandId={brandId} brandName={currentBrandName} />
            )}

            {isOwner && (
                <Link
                    to="/settings/users"
                    data-testid="users-access-link"
                    className="mt-6 flex items-center justify-between border border-mist rounded-sm bg-white p-5 hover:border-sage-dark transition-colors"
                >
                    <span className="flex items-center gap-3">
                        <UsersThree size={22} weight="light" className="text-sage-dark" />
                        <span>
                            <div className="font-serif text-xl">Users & Access</div>
                            <div className="text-xs text-neutral-500 mt-0.5">Manage team accounts, roles and brand assignments.</div>
                        </span>
                    </span>
                    <CaretRight size={16} weight="light" className="text-neutral-400" />
                </Link>
            )}

            {isEditor && (
                <SynchronizationPanel isOwner={isOwner} />
            )}

            {isOwner && (
                <JubelioIntegrationPanel />
            )}

            {isEditor && (
                <ActivityHistoryPanel isOwner={isOwner} />
            )}
        </div>
    );
}

function ToggleRow({ title, description, checked, onChange, testId }) {
    return (
        <div className="flex items-start justify-between gap-4">
            <div>
                <div className="text-sm font-medium text-ink">{title}</div>
                {description && <div className="text-xs text-neutral-500 mt-0.5">{description}</div>}
            </div>
            <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
        </div>
    );
}

function PasswordField({ label, value, onChange, testId }) {
    return (
        <label className="block">
            <span className="eyebrow">{label}</span>
            <input
                type="password"
                required
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="mt-1 w-full border border-mist rounded-sm px-3 py-2 bg-white text-sm focus:outline-none focus:border-sage-dark"
            />
        </label>
    );
}

/* ---------------- Category Management ---------------- */
function CategoryManagement({ brandId, brandName }) {
    const [open, setOpen] = useState(false);
    const [cats, setCats] = useState([]);
    const [newName, setNewName] = useState("");
    const [busy, setBusy] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [del, setDel] = useState(null); // { item, blocked, blockedMessage, busy }

    const load = async () => {
        try {
            const { data } = await api.get("/categories", { params: { brand: brandId } });
            setCats(data);
        } catch {}
    };
    useEffect(() => { load(); }, [brandId]);

    const addCategory = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setBusy(true);
        try {
            await api.post("/categories", { name: newName.trim(), brand: brandId });
            setNewName("");
            await load();
            toast.success("Category added");
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const updateCat = async (id, patch) => {
        try {
            await api.put(`/categories/${id}`, patch);
            await load();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    const saveEdit = async () => {
        if (!editingId || !editName.trim()) {
            setEditingId(null);
            return;
        }
        await updateCat(editingId, { name: editName.trim() });
        setEditingId(null);
        toast.success("Renamed");
    };

    const requestDelete = (c) => {
        setDel({ item: c, blocked: false, blockedMessage: "", busy: false });
    };

    const confirmDelete = async () => {
        if (!del?.item) return;
        setDel((s) => ({ ...s, busy: true }));
        try {
            await api.delete(`/categories/${del.item.id}`);
            setDel(null);
            await load();
            toast.success("Category deleted");
        } catch (ex) {
            const msg = formatApiError(ex?.response?.data?.detail) || "Failed to delete";
            // Backend returns "Reassign N product(s) before deleting this category."
            const match = /Reassign\s+(\d+)/i.exec(msg);
            if (match) {
                const n = parseInt(match[1], 10);
                setDel((s) => ({
                    ...s,
                    blocked: true,
                    blockedMessage: `This category is currently assigned to ${n} product${n === 1 ? "" : "s"}. Reassign those products before deleting this category.`,
                    busy: false,
                }));
            } else {
                toast.error(msg);
                setDel((s) => ({ ...s, busy: false }));
            }
        }
    };

    const move = async (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= cats.length) return;
        const a = cats[idx], b = cats[target];
        await Promise.all([
            api.put(`/categories/${a.id}`, { order: b.order }),
            api.put(`/categories/${b.id}`, { order: a.order }),
        ]);
        await load();
    };

    return (
        <section className="mt-6 border border-mist rounded-sm bg-white p-6" data-testid="section-category-management">
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
                data-testid="section-category-management-toggle"
                className="w-full flex items-center justify-between gap-3 flex-wrap text-left">
                <h2 className="font-serif text-xl md:text-2xl inline-flex items-center gap-2">
                    <Tag size={18} weight="light" className="text-sage-dark" /> Category Management
                </h2>
                <span className="flex items-center gap-2">
                    <span className="eyebrow">{brandName}</span>
                    <CaretRight size={16} weight="light" className={`shrink-0 text-neutral-400 transition-transform ${open ? "rotate-90" : ""}`} />
                </span>
            </button>
            <div className={open ? "" : "hidden"} data-testid="section-category-management-body">

            <form onSubmit={addCategory} className="mt-4 flex gap-2" data-testid="category-form">
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New category name" className="flex-1 border border-mist rounded-sm px-3 py-2 bg-white text-sm focus:border-sage-dark outline-none" data-testid="category-name-input" />
                <button type="submit" disabled={busy} data-testid="category-add-button" className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                    <Plus size={14} weight="bold" /> Add
                </button>
            </form>

            <ul className="mt-5 border border-mist rounded-sm divide-y divide-mist">
                {cats.length === 0 ? (
                    <li className="px-4 py-6 text-sm text-neutral-400">No categories yet.</li>
                ) : (
                    cats.map((c, i) => (
                        <li key={c.id} className="flex items-center justify-between gap-2 px-3 py-2.5" data-testid={`category-row-${c.name}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                <div className="flex flex-col">
                                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="text-neutral-400 hover:text-sage-dark disabled:opacity-30">
                                        <ArrowUp size={12} weight="light" />
                                    </button>
                                    <button type="button" onClick={() => move(i, 1)} disabled={i === cats.length - 1} aria-label="Move down" className="text-neutral-400 hover:text-sage-dark disabled:opacity-30">
                                        <ArrowDown size={12} weight="light" />
                                    </button>
                                </div>
                                {editingId === c.id ? (
                                    <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => e.key === "Enter" && saveEdit()} className="font-serif text-lg bg-transparent border-b border-sage-dark outline-none flex-1" />
                                ) : (
                                    <span className="font-serif text-lg truncate">
                                        {c.name}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button type="button" onClick={() => { setEditingId(c.id); setEditName(c.name); }} aria-label="Rename" data-testid={`category-edit-${c.name}`} className="text-neutral-400 hover:text-sage-dark p-1.5"><PencilSimple size={14} weight="light" /></button>
                                <button type="button" onClick={() => requestDelete(c)} aria-label="Delete" data-testid={`category-delete-${c.name}`} className="text-neutral-400 hover:text-red-600 p-1.5"><Trash size={14} weight="light" /></button>
                            </div>
                        </li>
                    ))
                )}
            </ul>
            <p className="text-xs text-neutral-500 mt-3">
                Deletion is permanent and blocked while products still use the category — reassign them first.
            </p>

            <ConfirmDeleteModal
                open={!!del}
                onClose={() => setDel(null)}
                onConfirm={confirmDelete}
                busy={del?.busy}
                title="Delete this category?"
                description={del?.item ? `“${del.item.name}” will be permanently removed. This action cannot be undone.` : ""}
                blocked={!!del?.blocked}
                blockedTitle="Category is still in use"
                blockedMessage={del?.blockedMessage}
                viewHref={del?.item ? `/catalog?category=${encodeURIComponent(del.item.name)}` : undefined}
            />
            </div>
        </section>
    );
}

/* ---------------- Catalog Structure (Heels & Tea Level 1 + featured brands) ---------------- */
function CatalogStructureManagement({ brandId, brandName }) {
    const [open, setOpen] = useState(false);
    const [groups, setGroups] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [newGroupName, setNewGroupName] = useState("");
    const [newGroupIcon, setNewGroupIcon] = useState("");
    const [newGroupKind, setNewGroupKind] = useState("region");
    const [newBrand, setNewBrand] = useState("");
    const [newBrandRegion, setNewBrandRegion] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/catalog/structure", { params: { brand: brandId } });
            setGroups(data.groups || []);
            setFeatured(data.featured || []);
        } catch {}
    };
    useEffect(() => { load(); }, [brandId]);

    const addGroup = async (e) => {
        e.preventDefault();
        if (!newGroupName.trim()) return;
        try {
            await api.post("/catalog/groups", {
                brand: brandId,
                name: newGroupName.trim(),
                icon: newGroupIcon.trim(),
                kind: newGroupKind,
            });
            setNewGroupName(""); setNewGroupIcon(""); setNewGroupKind("region");
            await load();
            toast.success("Group added");
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    const updateGroup = async (gid, patch) => {
        try {
            await api.put(`/catalog/groups/${gid}`, patch);
            await load();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    const [delGroup, setDelGroup] = useState(null); // { item, blocked, blockedMessage, busy }
    const requestDeleteGroup = (g) => setDelGroup({ item: g, blocked: false, blockedMessage: "", busy: false });
    const confirmDeleteGroup = async () => {
        if (!delGroup?.item) return;
        setDelGroup((s) => ({ ...s, busy: true }));
        try {
            await api.delete(`/catalog/groups/${delGroup.item.id}`);
            setDelGroup(null);
            await load();
            toast.success("Deleted");
        } catch (ex) {
            const msg = formatApiError(ex?.response?.data?.detail) || "Failed to delete";
            const match = /Reassign\s+(\d+)/i.exec(msg);
            if (match) {
                const n = parseInt(match[1], 10);
                const kind = delGroup.item.kind === "region" ? "region" : "collection";
                setDelGroup((s) => ({
                    ...s,
                    blocked: true,
                    blockedMessage: `This ${kind} is currently assigned to ${n} product${n === 1 ? "" : "s"}. Reassign those products before deleting this ${kind}.`,
                    busy: false,
                }));
            } else {
                toast.error(msg);
                setDelGroup((s) => ({ ...s, busy: false }));
            }
        }
    };

    const moveGroup = async (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= groups.length) return;
        const a = groups[idx], b = groups[target];
        await Promise.all([
            api.put(`/catalog/groups/${a.id}`, { order: b.order }),
            api.put(`/catalog/groups/${b.id}`, { order: a.order }),
        ]);
        await load();
    };

    const regions = groups.filter((g) => g.kind === "region" && !g.archived);

    const [delFeat, setDelFeat] = useState(null);
    const requestDeleteFeatured = (f) => setDelFeat({ item: f, busy: false });
    const confirmDeleteFeatured = async () => {
        if (!delFeat?.item) return;
        setDelFeat((s) => ({ ...s, busy: true }));
        try {
            await api.delete(`/catalog/featured-filters/${delFeat.item.id}`);
            setDelFeat(null);
            await load();
            toast.success("Removed");
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
            setDelFeat((s) => ({ ...s, busy: false }));
        }
    };

    const addFeatured = async (e) => {
        e.preventDefault();
        if (!newBrand.trim() || !newBrandRegion) return;
        try {
            await api.post("/catalog/featured-filters", {
                brand: brandId,
                region_slug: newBrandRegion,
                product_brand: newBrand.trim(),
                display_name: newBrand.trim(),
            });
            setNewBrand(""); setNewBrandRegion("");
            await load();
            toast.success("Featured brand added");
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    return (
        <section className="mt-6 border border-mist rounded-sm bg-white p-6" data-testid="section-catalog-structure">
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
                data-testid="section-catalog-structure-toggle"
                className="w-full flex items-center justify-between gap-3 flex-wrap text-left">
                <h2 className="font-serif text-xl md:text-2xl inline-flex items-center gap-2">
                    <Tag size={18} weight="light" className="text-sage-dark" /> Catalog Structure
                </h2>
                <span className="flex items-center gap-2">
                    <span className="eyebrow">{brandName} · Level 1</span>
                    <CaretRight size={16} weight="light" className={`shrink-0 text-neutral-400 transition-transform ${open ? "rotate-90" : ""}`} />
                </span>
            </button>
            <div className={open ? "" : "hidden"} data-testid="section-catalog-structure-body">
            <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                Level 1 groups appear as the top chip row on the Heels & Tea catalog. Regions are hard associations (one per product); Collections are soft tags (a product can be in Winter and Korea at the same time).
            </p>

            <form onSubmit={addGroup} className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="catalog-structure-form">
                <input value={newGroupIcon} onChange={(e) => setNewGroupIcon(e.target.value)} placeholder="🇦🇺" className="md:col-span-1 border border-mist rounded-sm px-3 py-2 bg-white text-sm focus:border-sage-dark outline-none" data-testid="group-icon-input" />
                <input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="Group name (e.g. Australia)" className="md:col-span-2 border border-mist rounded-sm px-3 py-2 bg-white text-sm focus:border-sage-dark outline-none" data-testid="group-name-input" />
                <select value={newGroupKind} onChange={(e) => setNewGroupKind(e.target.value)} className="md:col-span-1 border border-mist rounded-sm px-3 py-2 bg-white text-sm" data-testid="group-kind-input">
                    <option value="region">Region</option>
                    <option value="collection">Collection</option>
                </select>
                <button type="submit" data-testid="group-add-button" className="md:col-span-1 inline-flex items-center justify-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep">
                    <Plus size={14} weight="bold" /> Add
                </button>
            </form>

            <ul className="mt-5 border border-mist rounded-sm divide-y divide-mist">
                {groups.length === 0 ? (
                    <li className="px-4 py-6 text-sm text-neutral-400">No Level 1 groups yet.</li>
                ) : (
                    groups.map((g, i) => (
                        <li key={g.id} className="flex items-center justify-between gap-2 px-3 py-2.5" data-testid={`group-row-${g.slug}`}>
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="flex flex-col">
                                    <button type="button" onClick={() => moveGroup(i, -1)} disabled={i === 0} aria-label="Move up" className="text-neutral-400 hover:text-sage-dark disabled:opacity-30">
                                        <ArrowUp size={12} weight="light" />
                                    </button>
                                    <button type="button" onClick={() => moveGroup(i, 1)} disabled={i === groups.length - 1} aria-label="Move down" className="text-neutral-400 hover:text-sage-dark disabled:opacity-30">
                                        <ArrowDown size={12} weight="light" />
                                    </button>
                                </div>
                                <span className="font-serif text-lg truncate">
                                    <span className="mr-1">{g.icon}</span>{g.name}
                                    <span className="ml-2 text-[9px] uppercase tracking-widest text-neutral-400 border border-mist px-1.5 py-0.5 rounded-full align-middle">
                                        {g.kind}
                                    </span>
                                </span>
                            </div>
                            <button type="button" onClick={() => requestDeleteGroup(g)} aria-label="Delete" data-testid={`group-delete-${g.slug}`} className="text-neutral-400 hover:text-red-600 p-1.5">
                                <Trash size={14} weight="light" />
                            </button>
                        </li>
                    ))
                )}
            </ul>

            <div className="mt-6 pt-5 border-t border-mist">
                <h3 className="font-serif text-lg">Featured Brand Filters</h3>
                <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
                    Promote a product brand (e.g. EVE-A) as a Level 2 chip under a specific region. Products in that region whose Product Brand matches will show when the chip is active.
                </p>
                <form onSubmit={addFeatured} className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="featured-form">
                    <select value={newBrandRegion} onChange={(e) => setNewBrandRegion(e.target.value)} className="md:col-span-2 border border-mist rounded-sm px-3 py-2 bg-white text-sm" data-testid="featured-region-input">
                        <option value="">Select region…</option>
                        {regions.map((r) => (
                            <option key={r.id} value={r.slug}>{r.icon} {r.name}</option>
                        ))}
                    </select>
                    <input value={newBrand} onChange={(e) => setNewBrand(e.target.value)} placeholder="Brand (e.g. EVE-A)" className="md:col-span-2 border border-mist rounded-sm px-3 py-2 bg-white text-sm focus:border-sage-dark outline-none" data-testid="featured-brand-input" />
                    <button type="submit" data-testid="featured-add-button" className="md:col-span-1 inline-flex items-center justify-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep">
                        <Plus size={14} weight="bold" /> Add
                    </button>
                </form>
                <ul className="mt-4 border border-mist rounded-sm divide-y divide-mist">
                    {featured.length === 0 ? (
                        <li className="px-4 py-6 text-sm text-neutral-400">No featured brand filters.</li>
                    ) : (
                        featured.map((f) => (
                            <li key={f.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`featured-row-${f.product_brand}`}>
                                <span className="text-sm">
                                    <span className="eyebrow text-neutral-400 mr-2">{f.region_slug}</span>
                                    <span className="text-ink">{f.display_name}</span>
                                </span>
                                <button type="button" onClick={() => requestDeleteFeatured(f)} aria-label="Remove" data-testid={`featured-delete-${f.product_brand}`} className="text-neutral-400 hover:text-red-600 p-1.5">
                                    <Trash size={14} weight="light" />
                                </button>
                            </li>
                        ))
                    )}
                </ul>
            </div>

            <ConfirmDeleteModal
                open={!!delGroup}
                onClose={() => setDelGroup(null)}
                onConfirm={confirmDeleteGroup}
                busy={delGroup?.busy}
                title={delGroup?.item ? `Delete this ${delGroup.item.kind}?` : "Delete?"}
                description={delGroup?.item ? `“${delGroup.item.name}” will be permanently removed. This action cannot be undone.` : ""}
                blocked={!!delGroup?.blocked}
                blockedTitle={delGroup?.item?.kind === "region" ? "Region is still in use" : "Collection is still in use"}
                blockedMessage={delGroup?.blockedMessage}
                viewHref={delGroup?.item ? (delGroup.item.kind === "region"
                    ? `/catalog?region=${encodeURIComponent(delGroup.item.slug)}`
                    : `/catalog?collection=${encodeURIComponent(delGroup.item.slug)}`) : undefined}
            />

            <ConfirmDeleteModal
                open={!!delFeat}
                onClose={() => setDelFeat(null)}
                onConfirm={confirmDeleteFeatured}
                busy={delFeat?.busy}
                title="Remove this featured filter?"
                description={delFeat?.item
                    ? `“${delFeat.item.display_name}” will no longer appear as a Level 2 shortcut under ${delFeat.item.region_slug}. Existing products and the “${delFeat.item.product_brand}” Product Brand remain untouched.`
                    : ""}
                confirmLabel="Remove"
            />
            </div>
        </section>
    );
}

/* ---------------- Synchronization Panel ---------------- */
function SynchronizationPanel({ isOwner = false }) {
    const [open, setOpen] = useState(false);
    const [history, setHistory] = useState(null);
    const [syncing, setSyncing] = useState(false);

    const latest = history?.runs?.[0] || null;
    const lastSuccess = history?.last_successful || null;
    const inProgress = Boolean(history?.in_progress);

    const loadHistory = async () => {
        try {
            const { data } = await api.get("/jubelio/sync-history");
            setHistory(data);
        } catch {
            setHistory({ runs: [], last_successful: null, in_progress: false });
        }
    };
    useEffect(() => { loadHistory(); }, []);

    const runSyncNow = async () => {
        if (!isOwner || syncing || inProgress) return;
        setSyncing(true);
        const clickedAt = Date.now();
        try {
            // Fire-and-forget: backend accepts the sync and runs it in a
            // BackgroundTask (manual syncs take ~90s on larger mapping sets,
            // longer than the ingress timeout). Poll /sync-history until a
            // run started AFTER our click reaches a terminal status.
            await api.post("/jubelio/sync-now");

            const { toast } = await import("sonner");
            const started = Date.now();
            const DEADLINE_MS = 3 * 60 * 1000;
            let finalRun = null;
            while (Date.now() - started < DEADLINE_MS) {
                await new Promise((r) => setTimeout(r, 2000));
                try {
                    const { data } = await api.get("/jubelio/sync-history");
                    setHistory(data);
                    const latest = data?.runs?.[0];
                    const latestStartedAt = latest?.started_at
                        ? Date.parse(latest.started_at)
                        : 0;
                    // Our run has reached a terminal state when:
                    //   - no sync is in progress, AND
                    //   - the newest run on the server started after we clicked
                    if (!data?.in_progress && latestStartedAt >= clickedAt - 2000 && latest?.status && latest.status !== "running") {
                        finalRun = latest;
                        break;
                    }
                } catch {
                    // transient network — keep polling
                }
            }
            if (finalRun) {
                toast.success(
                    `Sync ${finalRun.status} · checked ${finalRun.checked ?? 0}, updated ${finalRun.updated ?? 0}, unchanged ${finalRun.unchanged ?? 0}, failed ${finalRun.failed ?? 0}`
                );
            } else {
                toast("Sync still running — refresh in a moment to see results.");
            }
        } catch (ex) {
            const { toast } = await import("sonner");
            toast.error(ex?.response?.data?.detail || "Sync failed to start");
        } finally {
            setSyncing(false);
        }
    };

    const fmtTime = (iso) => {
        if (!iso) return "—";
        try {
            // Backend stores UTC in Mongo but returns ISO strings without a
            // timezone suffix (`YYYY-MM-DDTHH:mm:ss.ffffff`). JavaScript's
            // `new Date()` would interpret that as LOCAL time, producing a
            // 7-hour mislabel for Jakarta users. Force UTC parsing when the
            // string carries no explicit offset, then render in Asia/Jakarta
            // so every viewer sees WIB regardless of their device timezone.
            const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
            const d = new Date(hasTz ? iso : `${iso}Z`);
            return d.toLocaleString("en-GB", {
                dateStyle: "medium",
                timeStyle: "short",
                timeZone: "Asia/Jakarta",
            });
        } catch {
            return String(iso);
        }
    };
    const roleLabel = isOwner ? "Owner" : "View only";

    return (
        <section className="mt-6 border border-mist rounded-sm bg-white p-6" data-testid="section-synchronization">
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
                data-testid="section-synchronization-toggle"
                className="w-full flex items-center justify-between gap-3 flex-wrap text-left">
                <h2 className="font-serif text-xl md:text-2xl inline-flex items-center gap-2">
                    <ArrowsClockwise size={18} weight="light" className="text-sage-dark" /> Synchronization
                </h2>
                <span className="flex items-center gap-2">
                    <span className="eyebrow">{roleLabel}</span>
                    <CaretRight size={16} weight="light" className={`shrink-0 text-neutral-400 transition-transform ${open ? "rotate-90" : ""}`} />
                </span>
            </button>
            <div className={open ? "" : "hidden"} data-testid="section-synchronization-body">
                <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                    Mapped products automatically synchronize stock and retail price from Jubelio. Stock is sourced from KAPUK OFFICE (KP04).
                </p>

                {/* Status strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-sm">
                    <Stat label="Last Synced" value={fmtTime(lastSuccess?.finished_at)} />
                    <Stat label="Status" value={inProgress ? "running" : (latest?.status || "never_synced")} />
                    <Stat label="Checked" value={latest?.checked ?? "—"} />
                    <Stat label="Updated" value={latest?.updated ?? "—"} />
                    <Stat label="Unchanged" value={latest?.unchanged ?? "—"} />
                    <Stat label="Skipped" value={latest?.skipped ?? "—"} />
                    <Stat label="Failed" value={latest?.failed ?? "—"} />
                    <Stat label="Warehouse" value={latest?.location_code || "KP04"} />
                </div>

                {/* Sync Now (Owner only) */}
                {isOwner && (
                    <button
                        type="button"
                        onClick={runSyncNow}
                        disabled={syncing || inProgress}
                        data-testid="sync-trigger"
                        className="mt-5 inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-50"
                    >
                        <ArrowsClockwise size={14} weight={syncing || inProgress ? "bold" : "light"} className={syncing || inProgress ? "animate-spin" : ""} />
                        {syncing || inProgress ? "Syncing…" : "Sync now"}
                    </button>
                )}

                {/* Auto Sync Schedule */}
                <div className="mt-6 border border-mist rounded-sm p-4 bg-cream/40" data-testid="sync-schedule">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="eyebrow text-sage-deep">Auto Sync · Active</span>
                        <span className="text-xs text-neutral-500">Asia/Jakarta (WIB)</span>
                    </div>
                    <div className="mt-2 font-sans text-sm text-neutral-700 tracking-wide">
                        00:00 · 06:00 · 09:00 · 12:00 · 15:00 · 18:00 · 21:00
                    </div>
                </div>

                {/* Recent history */}
                <div className="mt-6" data-testid="sync-history">
                    <div className="eyebrow mb-2">Recent Runs</div>
                    {history?.runs?.length ? (
                        <div className="overflow-x-auto no-scrollbar">
                            <table className="w-full text-xs border border-mist rounded-sm">
                                <thead className="bg-cream/60 text-neutral-500 uppercase tracking-widest">
                                    <tr>
                                        <th className="text-left px-3 py-2">When</th>
                                        <th className="text-left px-3 py-2">Trigger</th>
                                        <th className="text-right px-3 py-2">Checked</th>
                                        <th className="text-right px-3 py-2">Updated</th>
                                        <th className="text-right px-3 py-2">Unchanged</th>
                                        <th className="text-right px-3 py-2">Failed</th>
                                        <th className="text-left px-3 py-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.runs.slice(0, 10).map((r) => (
                                        <tr key={r.id} className="border-t border-mist" data-testid={`sync-run-row-${r.id}`}>
                                            <td className="px-3 py-2 font-sans">{fmtTime(r.started_at)}</td>
                                            <td className="px-3 py-2 uppercase tracking-widest text-neutral-500">{r.trigger}</td>
                                            <td className="px-3 py-2 text-right">{r.checked ?? "—"}</td>
                                            <td className="px-3 py-2 text-right">{r.updated ?? "—"}</td>
                                            <td className="px-3 py-2 text-right">{r.unchanged ?? "—"}</td>
                                            <td className="px-3 py-2 text-right">{r.failed ?? "—"}</td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${
                                                    r.status === "ok" ? "bg-sage-soft text-sage-deep" : r.status === "running" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"
                                                }`}>{r.status}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-xs text-neutral-500">No sync runs yet.</p>
                    )}
                </div>
            </div>
        </section>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border border-mist rounded-sm p-3">
            <div className="eyebrow">{label}</div>
            <div className="font-sans text-base mt-1 break-all">{String(value)}</div>
        </div>
    );
}

/**
 * Jubelio Integration — Phase 1 (owner-only, READ-ONLY).
 *
 * Lets the Owner test the Jubelio connection, pick ONE warehouse, and run a
 * SKU → stock comparison. Nothing here writes to Jubelio and nothing here
 * modifies Emergent product stock.
 */
function JubelioIntegrationPanel() {
    const [sectionOpen, setSectionOpen] = useState(false);
    const [status, setStatus] = useState(null);
    const [locations, setLocations] = useState([]);
    const [busy, setBusy] = useState(false);
    const [skuInput, setSkuInput] = useState("");
    const [testResults, setTestResults] = useState(null);
    const [stockTestOpen, setStockTestOpen] = useState(false);

    // ----- Phase 2: Selective Product Import state -----
    const [impOpen, setImpOpen] = useState(false);
    const [impLoading, setImpLoading] = useState(false);
    const [impPage, setImpPage] = useState(1);
    const [impPageSize, setImpPageSize] = useState(25);
    const [impQuery, setImpQuery] = useState("");
    const [impQueryInput, setImpQueryInput] = useState("");
    const [impData, setImpData] = useState(null);
    const [impSelected, setImpSelected] = useState({}); // key=item_id, val=destination brand
    const [impBatchBrand, setImpBatchBrand] = useState("hoshizora");
    const [impBatchCategory, setImpBatchCategory] = useState("");
    const [impCategories, setImpCategories] = useState([]); // categories of impBatchBrand
    const [impPreview, setImpPreview] = useState(null);
    const [impCommitting, setImpCommitting] = useState(false);

    const refresh = async () => {
        try { const { data } = await api.get("/jubelio/status"); setStatus(data); } catch {}
    };
    useEffect(() => { refresh(); }, []);

    const testConnection = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/jubelio/test-connection");
            setStatus(data);
            toast.success("Jubelio connection verified");
            try {
                const r = await api.get("/jubelio/locations");
                setLocations(r.data?.locations || []);
            } catch {}
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Connection failed");
            await refresh();
        } finally { setBusy(false); }
    };

    const loadLocations = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/jubelio/locations");
            setLocations(data.locations || []);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Could not load locations");
        } finally { setBusy(false); }
    };

    const selectLocation = async (loc) => {
        setBusy(true);
        try {
            const { data } = await api.put("/jubelio/location", {
                location_id: loc.location_id,
                location_code: loc.location_code,
                location_name: loc.location_name,
            });
            setStatus(data);
            toast.success(`Warehouse set to ${formatWarehouse(loc.location_name, loc.location_code, loc.location_id)}`);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Save failed");
        } finally { setBusy(false); }
    };

    const formatWarehouse = (name, code, id) => {
        const n = (name || "").toString().trim();
        const c = (code || "").toString().trim();
        if (n && c) return `${n} (${c})`;
        if (n) return n;
        if (c) return c;
        return id ? `#${id}` : "";
    };

    const runStockTest = async () => {
        const skus = skuInput.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
        if (!skus.length) { toast.error("Enter one SKU per line"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/jubelio/stock-test", { skus });
            setTestResults(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Test failed");
        } finally { setBusy(false); }
    };

    // ----- Phase 2 helpers -----
    useEffect(() => {
        if (!impOpen) return;
        (async () => {
            try {
                const { data } = await api.get("/categories", { params: { brand: impBatchBrand } });
                const active = (data || []).filter((c) => !c.archived);
                setImpCategories(active);
                // Reset chosen category whenever brand changes to prevent sending a stale value.
                setImpBatchCategory((prev) => (active.find((c) => c.name === prev) ? prev : ""));
            } catch {
                setImpCategories([]);
                setImpBatchCategory("");
            }
        })();
    }, [impBatchBrand, impOpen]);

    const loadCandidates = async (opts = {}) => {
        if (!status?.location_id) { toast.error("Pick a warehouse first"); return; }
        setImpLoading(true);
        try {
            const page = opts.page ?? impPage;
            const page_size = opts.page_size ?? impPageSize;
            const q = opts.q !== undefined ? opts.q : impQuery;
            const { data } = await api.get("/jubelio/products/candidates", {
                params: { page, page_size, q: q || undefined },
            });
            setImpData(data);
            setImpPage(data.page || page);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Could not load Jubelio products");
        } finally {
            setImpLoading(false);
        }
    };

    const isSelectable = (c) => !!c.jubelio_item_code && c.emergent_status !== "CONFLICT" && c.emergent_status !== "MISSING_SKU" && !c.existing_mapping;

    const toggleRow = (c, checked) => {
        if (!isSelectable(c)) return;
        setImpSelected((prev) => {
            const next = { ...prev };
            if (checked) {
                next[c.jubelio_item_id] = {
                    row: c,
                    brand: next[c.jubelio_item_id]?.brand || impBatchBrand,
                };
            } else {
                delete next[c.jubelio_item_id];
            }
            return next;
        });
    };

    const selectedCount = Object.keys(impSelected).length;
    const pageEligible = (impData?.candidates || []).filter(isSelectable);
    const pageSelectedCount = pageEligible.filter((c) => impSelected[c.jubelio_item_id]).length;
    const allPageSelected = pageEligible.length > 0 && pageSelectedCount === pageEligible.length;

    const togglePage = (checked) => {
        setImpSelected((prev) => {
            const next = { ...prev };
            pageEligible.forEach((c) => {
                if (checked) next[c.jubelio_item_id] = { row: c, brand: next[c.jubelio_item_id]?.brand || impBatchBrand };
                else delete next[c.jubelio_item_id];
            });
            return next;
        });
    };

    const previewImport = async () => {
        if (!selectedCount) { toast.error("Select at least one product"); return; }
        const anyNewCandidate = Object.values(impSelected).some(({ row }) =>
            !row.existing_mapping && row.emergent_status !== "MATCHED" && row.emergent_status !== "CONFLICT" && row.emergent_status !== "MISSING_SKU" && !!row.jubelio_item_code
        );
        if (anyNewCandidate && !impBatchCategory) {
            toast.error("Pick a category for new products before preview");
            return;
        }
        const items = Object.values(impSelected).map(({ row }) => ({
            jubelio_item_id: row.jubelio_item_id,
            jubelio_item_code: row.jubelio_item_code,
            name: row.name,
            brand_name: row.brand_name,
            thumbnail: row.thumbnail,
            selected_warehouse_stock: row.selected_warehouse_stock,
        }));
        setImpCommitting(true);
        try {
            const { data } = await api.post("/jubelio/products/import", {
                items, destination_brand: impBatchBrand,
                destination_category: impBatchCategory || null,
                confirm: false,
            });
            setImpPreview(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Preview failed");
        } finally { setImpCommitting(false); }
    };

    const confirmImport = async () => {
        if (!impPreview) return;
        const items = Object.values(impSelected).map(({ row }) => ({
            jubelio_item_id: row.jubelio_item_id,
            jubelio_item_code: row.jubelio_item_code,
            name: row.name,
            brand_name: row.brand_name,
            thumbnail: row.thumbnail,
            selected_warehouse_stock: row.selected_warehouse_stock,
        }));
        setImpCommitting(true);
        try {
            const { data } = await api.post("/jubelio/products/import", {
                items, destination_brand: impBatchBrand,
                destination_category: impBatchCategory || null,
                confirm: true,
            });
            const s = data.summary || {};
            toast.success(`Done — ${s.new || 0} created · ${s.matched || 0} mapped · ${s.conflict || 0} conflict · ${s.missing_sku || 0} skipped`);
            setImpPreview(null);
            setImpSelected({});
            await loadCandidates(); // refresh so MATCHED rows now show mapping chip
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Import failed");
        } finally { setImpCommitting(false); }
    };

    const statusChipClass = (s) => {
        if (s === "NEW") return "bg-sage-soft text-sage-deep";
        if (s === "MATCHED") return "bg-amber-100 text-amber-800";
        if (s === "CONFLICT") return "bg-red-100 text-red-700";
        if (s === "MISSING_SKU") return "bg-neutral-100 text-neutral-500";
        return "bg-neutral-100 text-neutral-600";
    };

    return (
        <section className="mt-6 border border-mist rounded-sm bg-white p-6" data-testid="section-jubelio">
            <button type="button" onClick={() => setSectionOpen((o) => !o)} aria-expanded={sectionOpen}
                data-testid="section-jubelio-toggle"
                className="w-full flex items-center justify-between gap-3 flex-wrap text-left">
                <h2 className="font-serif text-xl md:text-2xl inline-flex items-center gap-2">
                    <ArrowsClockwise size={18} weight="light" className="text-sage-dark" /> Jubelio Integration
                </h2>
                <span className="flex items-center gap-2">
                    <span className="eyebrow">Owner · Phase 1 · read-only</span>
                    <CaretRight size={16} weight="light" className={`shrink-0 text-neutral-400 transition-transform ${sectionOpen ? "rotate-90" : ""}`} />
                </span>
            </button>
            <div className={sectionOpen ? "" : "hidden"} data-testid="section-jubelio-body">
            <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                Compares Emergent SKUs against Jubelio `item_code` for the selected warehouse. No stock is overwritten in either system.
            </p>

            <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
                <Stat label="Status" value={status?.connection_status || "—"} />
                <Stat label="Last connected" value={status?.last_connected_at ? new Date(status.last_connected_at).toLocaleString() : "—"} />
                <Stat label="Warehouse" value={formatWarehouse(status?.location_name, status?.location_code, status?.location_id) || "— not selected"} />
                <Stat label="Credentials" value={status?.credentials_present ? "configured" : "missing"} />
            </div>

            <div className="mt-4 flex gap-2 flex-wrap">
                <button type="button" onClick={testConnection} disabled={busy} data-testid="jubelio-test-connection"
                    className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                    Test connection
                </button>
                <button type="button" onClick={loadLocations} disabled={busy} data-testid="jubelio-load-locations"
                    className="inline-flex items-center gap-1 border border-mist text-neutral-700 px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:border-sage-dark disabled:opacity-60">
                    Load warehouses
                </button>
            </div>

            {locations.length > 0 && (
                <div className="mt-4" data-testid="jubelio-warehouses">
                    <div className="eyebrow mb-2">Pick a warehouse</div>
                    <div className="flex flex-wrap gap-2">
                        {locations.map((l) => {
                            const active = String(status?.location_id) === String(l.location_id);
                            return (
                                <button key={l.location_id} type="button" onClick={() => selectLocation(l)}
                                    data-testid={`jubelio-warehouse-${l.location_code || l.location_id}`}
                                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs uppercase tracking-widest transition-colors ${active ? "bg-sage-dark text-white border border-sage-dark" : "border border-mist text-neutral-600 hover:border-sage-dark"}`}>
                                    {formatWarehouse(l.location_name, l.location_code, l.location_id)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="mt-6 border-t border-mist pt-5" data-testid="stock-test-section">
                <button type="button" onClick={() => setStockTestOpen((o) => !o)}
                    data-testid="stock-test-toggle" aria-expanded={stockTestOpen}
                    className="w-full flex items-center justify-between gap-2 text-left">
                    <div className="eyebrow">Advanced · Stock Matching Test</div>
                    <CaretRight size={16} weight="light" className={`shrink-0 transition-transform ${stockTestOpen ? "rotate-90" : ""}`} />
                </button>
                {stockTestOpen && (<div className="mt-3" data-testid="stock-test-panel">
                <p className="text-xs text-neutral-500">Enter 3–5 Emergent SKUs (comma or newline separated). This compares Emergent stock against Jubelio `location_stocks.available` for the selected warehouse. Nothing is written anywhere.</p>
                <textarea rows={3} value={skuInput} onChange={(e) => setSkuInput(e.target.value)} placeholder="SSR-001&#10;HDN-001&#10;TB-001"
                    data-testid="jubelio-sku-input"
                    className="mt-2 w-full border border-mist rounded-sm px-3 py-2 bg-white font-mono text-sm focus:outline-none focus:border-sage-dark" />
                <button type="button" onClick={runStockTest} disabled={busy || !status?.location_id}
                    data-testid="jubelio-run-stock-test"
                    className="mt-2 inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                    Run Stock Matching Test
                </button>
                {!status?.location_id && <p className="text-xs text-amber-600 mt-2">Pick a warehouse first.</p>}

                {testResults && (
                    <div className="mt-5 border border-mist rounded-sm overflow-x-auto" data-testid="jubelio-test-results">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left border-b border-mist bg-[#FAF9F6]">
                                    {["SKU", "Product", "Brand", "Emergent", "Jubelio", "Status", "Detail"].map((h) => <th key={h} className="eyebrow font-normal px-3 py-2">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {testResults.results.map((r) => (
                                    <tr key={r.sku} className="border-b border-mist" data-testid={`jubelio-result-${r.sku}`}>
                                        <td className="px-3 py-2 font-mono">{r.sku}</td>
                                        <td className="px-3 py-2">{r.emergent_name || r.jubelio_item_name || "—"}</td>
                                        <td className="px-3 py-2">{r.emergent_brand || "—"}</td>
                                        <td className="px-3 py-2 tabular-nums">{r.emergent_stock ?? "—"}</td>
                                        <td className="px-3 py-2 tabular-nums">{r.jubelio_available ?? "—"}</td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${r.status === "MATCH" ? "bg-sage-soft text-sage-deep" : r.status === "DIFFERENT" ? "bg-amber-100 text-amber-800" : "bg-neutral-100 text-neutral-600"}`}>{r.status}</span>
                                        </td>
                                        <td className="px-3 py-2 text-neutral-500">{r.detail || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                </div>)}
            </div>

            {/* ---------- Phase 2: Jubelio Product Import (Owner only) ---------- */}
            <div className="mt-6 border-t border-mist pt-5" data-testid="jubelio-import-section">
                <button type="button" onClick={() => setImpOpen((o) => !o)}
                    data-testid="jubelio-import-toggle"
                    className="w-full flex items-center justify-between gap-2 text-left">
                    <div>
                        <div className="eyebrow">Jubelio Product Import</div>
                        <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
                            Owner-only. Selectively bring Jubelio items into this catalog or map them to existing products.
                            Nothing imports until you confirm. Jubelio stays read-only.
                        </p>
                    </div>
                    <CaretRight size={16} weight="light" className={`shrink-0 transition-transform ${impOpen ? "rotate-90" : ""}`} />
                </button>

                {impOpen && (
                    <div className="mt-4" data-testid="jubelio-import-panel">
                        <div className="flex flex-wrap items-center gap-2">
                            <input value={impQueryInput} onChange={(e) => setImpQueryInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { setImpQuery(impQueryInput); loadCandidates({ page: 1, q: impQueryInput }); } }}
                                placeholder="Search Jubelio SKU / name"
                                data-testid="jubelio-import-search"
                                className="flex-1 min-w-[200px] border border-mist rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:border-sage-dark" />
                            <select value={impPageSize} onChange={(e) => { const n = parseInt(e.target.value, 10); setImpPageSize(n); setImpQuery(impQueryInput); loadCandidates({ page: 1, page_size: n, q: impQueryInput }); }}
                                data-testid="jubelio-import-page-size"
                                className="border border-mist rounded-sm px-2 py-2 text-sm bg-white">
                                <option value={25}>25 / page</option>
                                <option value={50}>50 / page</option>
                                <option value={100}>100 / page</option>
                            </select>
                            <button type="button" onClick={() => { setImpQuery(impQueryInput); loadCandidates({ page: 1, q: impQueryInput }); }} disabled={impLoading || !status?.location_id}
                                data-testid="jubelio-import-load"
                                className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                                {impLoading ? "Loading…" : "Load Jubelio products"}
                            </button>
                        </div>
                        {!status?.location_id && <p className="text-xs text-amber-600 mt-2">Pick a warehouse first.</p>}

                        {impData && (
                            <>
                                <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
                                    <div className="text-xs text-neutral-500">
                                        Showing page {impData.page} · {impData.candidates?.length || 0} rows
                                        {impData.total != null && <> · total {impData.total}</>}
                                        {impData.selected_location_name && <> · stock from <strong>{impData.selected_location_name} ({impData.selected_location_code})</strong></>}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="eyebrow">Assign selection to:</span>
                                        <select value={impBatchBrand} onChange={(e) => setImpBatchBrand(e.target.value)}
                                            data-testid="jubelio-import-batch-brand"
                                            className="border border-mist rounded-sm px-2 py-1 text-sm bg-white">
                                            <option value="hoshizora">Hoshizora</option>
                                            <option value="heelsandtea">Heels & Tea</option>
                                        </select>
                                        <select value={impBatchCategory} onChange={(e) => setImpBatchCategory(e.target.value)}
                                            data-testid="jubelio-import-batch-category"
                                            className="border border-mist rounded-sm px-2 py-1 text-sm bg-white">
                                            <option value="">— Select a category —</option>
                                            {impCategories.map((c) => (
                                                <option key={c.id} value={c.name}>{c.name}</option>
                                            ))}
                                        </select>
                                        <button type="button" onClick={previewImport} disabled={!selectedCount || impCommitting}
                                            data-testid="jubelio-import-preview"
                                            className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                                            Preview import ({selectedCount})
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-3 border border-mist rounded-sm overflow-x-auto">
                                    <table className="w-full text-xs min-w-[720px]" data-testid="jubelio-import-table">
                                        <thead>
                                            <tr className="text-left border-b border-mist bg-[#FAF9F6]">
                                                <th className="px-2 py-2 w-8">
                                                    <input type="checkbox"
                                                        checked={allPageSelected}
                                                        disabled={pageEligible.length === 0}
                                                        ref={(el) => { if (el) el.indeterminate = pageSelectedCount > 0 && !allPageSelected; }}
                                                        onChange={(e) => togglePage(e.target.checked)}
                                                        title="Select all eligible rows on this page"
                                                        aria-label="Select all eligible rows on this page"
                                                        data-testid="jubelio-import-select-page" />
                                                </th>
                                                <th className="px-2 py-2 w-12">Image</th>
                                                <th className="eyebrow font-normal px-2 py-2">Name</th>
                                                <th className="eyebrow font-normal px-2 py-2">SKU</th>
                                                <th className="eyebrow font-normal px-2 py-2">Jubelio brand</th>
                                                <th className="eyebrow font-normal px-2 py-2">Stock (Selected Warehouse)</th>
                                                <th className="eyebrow font-normal px-2 py-2">Status</th>
                                                <th className="eyebrow font-normal px-2 py-2">Destination</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(impData.candidates || []).map((c) => {
                                                const key = c.jubelio_item_id;
                                                const disabled = !isSelectable(c);
                                                const checked = !!impSelected[key];
                                                return (
                                                    <tr key={key} className="border-b border-mist align-top"
                                                        data-testid={`jubelio-cand-${c.jubelio_item_code || c.jubelio_item_id}`}>
                                                        <td className="px-2 py-2">
                                                            <input type="checkbox" checked={checked} disabled={disabled}
                                                                onChange={(e) => toggleRow(c, e.target.checked)}
                                                                data-testid={`jubelio-cand-check-${c.jubelio_item_code || c.jubelio_item_id}`} />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            {c.thumbnail
                                                                ? <img src={c.thumbnail} alt="" className="w-10 h-10 object-cover rounded-sm bg-neutral-100" />
                                                                : <div className="w-10 h-10 bg-neutral-100 rounded-sm" />}
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <div className="line-clamp-2">{c.name || "—"}</div>
                                                            {c.variant && <div className="text-[10px] text-neutral-500 mt-0.5">{Array.isArray(c.variant) ? c.variant.map((v) => (v && typeof v === "object" ? `${v.label || ""}: ${v.value || ""}` : String(v))).join(", ") : (typeof c.variant === "object" ? `${c.variant.label || ""}: ${c.variant.value || ""}` : String(c.variant))}</div>}
                                                        </td>
                                                        <td className="px-2 py-2 font-mono">{c.jubelio_item_code || <span className="text-neutral-400">—</span>}</td>
                                                        <td className="px-2 py-2">{c.brand_name || "—"}</td>
                                                        <td className="px-2 py-2 tabular-nums">{c.selected_warehouse_stock ?? "—"}</td>
                                                        <td className="px-2 py-2">
                                                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${statusChipClass(c.emergent_status)}`}>
                                                                {(c.emergent_status || "UNKNOWN").replace("_", " ")}
                                                            </span>
                                                            {c.existing_mapping && <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-sage-soft text-sage-deep" data-testid={`jubelio-cand-mapped-${c.jubelio_item_code}`}>Mapped</span>}
                                                            {c.status_detail && <div className="text-[10px] text-neutral-500 mt-1">{c.status_detail}</div>}
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            {checked ? (
                                                                <select value={impSelected[key]?.brand || impBatchBrand}
                                                                    onChange={(e) => setImpSelected((p) => ({ ...p, [key]: { ...p[key], brand: e.target.value } }))}
                                                                    data-testid={`jubelio-cand-brand-${c.jubelio_item_code}`}
                                                                    className="border border-mist rounded-sm px-2 py-1 text-xs bg-white">
                                                                    <option value="hoshizora">Hoshizora</option>
                                                                    <option value="heelsandtea">Heels & Tea</option>
                                                                </select>
                                                            ) : (c.existing_mapping ? <span className="text-xs text-neutral-500">{c.existing_mapping.brand}</span> : <span className="text-xs text-neutral-400">—</span>)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {!impData.candidates?.length && (
                                                <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-neutral-500">No Jubelio products returned.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-3 flex items-center justify-between gap-2">
                                    <button type="button" onClick={() => loadCandidates({ page: Math.max(1, impPage - 1), q: impQueryInput })} disabled={impLoading || impPage <= 1}
                                        data-testid="jubelio-import-prev"
                                        className="text-xs uppercase tracking-widest border border-mist px-3 py-1 rounded-sm disabled:opacity-40">← Prev</button>
                                    <span className="text-xs text-neutral-500">Page {impPage}</span>
                                    <button type="button" onClick={() => loadCandidates({ page: impPage + 1, q: impQueryInput })} disabled={impLoading || !impData?.has_next}
                                        data-testid="jubelio-import-next"
                                        className="text-xs uppercase tracking-widest border border-mist px-3 py-1 rounded-sm disabled:opacity-40">Next →</button>
                                </div>
                            </>
                        )}

                        {impPreview && (
                            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="jubelio-import-confirm-modal">
                                <div className="bg-white rounded-sm shadow-lg max-w-md w-full p-6">
                                    <h3 className="font-serif text-xl mb-1">Confirm Import</h3>
                                    <p className="text-xs text-neutral-500 mb-4">
                                        Destination brand: <strong>{impPreview.destination_brand === "hoshizora" ? "Hoshizora" : "Heels & Tea"}</strong>
                                        {impPreview.destination_category && (
                                            <> · Category for new products: <strong data-testid="confirm-category">{impPreview.destination_category}</strong></>
                                        )}
                                        . Only confirmed rows will be committed. Jubelio stays read-only.
                                    </p>
                                    <ul className="text-sm space-y-1 mb-4">
                                        <li>Selected: <strong data-testid="confirm-total">{Object.keys(impSelected).length}</strong></li>
                                        <li>New products to create: <strong data-testid="confirm-new">{impPreview.summary.new}</strong></li>
                                        <li>Existing SKU matches (map only): <strong data-testid="confirm-matched">{impPreview.summary.matched}</strong></li>
                                        <li>Conflicts (skipped): <strong data-testid="confirm-conflict">{impPreview.summary.conflict}</strong></li>
                                        <li>Missing SKU (skipped): <strong data-testid="confirm-missing">{impPreview.summary.missing_sku}</strong></li>
                                    </ul>
                                    {(impPreview.processed || []).some((r) => r.status === "NEW") && (
                                        <div className="mb-4 border-t border-mist pt-3" data-testid="confirm-new-prices">
                                            <div className="eyebrow mb-2">New product prices (from Jubelio sell_price)</div>
                                            <ul className="text-xs space-y-1 max-h-40 overflow-auto">
                                                {(impPreview.processed || []).filter((r) => r.status === "NEW").map((r) => (
                                                    <li key={r.jubelio_item_id} className="flex items-start justify-between gap-2"
                                                        data-testid={`confirm-price-${r.jubelio_item_code}`}>
                                                        <span className="truncate"><span className="font-mono text-[11px]">{r.jubelio_item_code}</span> · {r.name}</span>
                                                        {r.price_needs_review ? (
                                                            <span className="shrink-0 text-amber-700" title={r.price_note || ""}>⚠ Review</span>
                                                        ) : (
                                                            <span className="shrink-0 tabular-nums">Rp {Number(r.retail_price || 0).toLocaleString("id-ID")}</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                            {(impPreview.processed || []).some((r) => r.status === "NEW" && r.price_needs_review) && (
                                                <p className="text-[11px] text-amber-700 mt-2">Items marked ⚠ Review will import with Retail Price = 0 and stay Draft. Set the price manually before publishing.</p>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex justify-end gap-2">
                                        <button type="button" onClick={() => setImpPreview(null)} disabled={impCommitting}
                                            data-testid="jubelio-import-cancel"
                                            className="border border-mist text-neutral-700 px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:border-sage-dark disabled:opacity-60">Cancel</button>
                                        <button type="button" onClick={confirmImport} disabled={impCommitting}
                                            data-testid="jubelio-import-confirm"
                                            className="bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                                            {impCommitting ? "Importing…" : "Confirm import"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            </div>
        </section>
    );
}

/**
 * Activity History panel — real append-only audit log.
 *
 * Owner sees everything with filters; Admin Input is read-only and backend-
 * scoped to assigned brands. No edit/delete/clear controls at any role.
 */
function ActivityHistoryPanel({ isOwner = false }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [brand, setBrand] = useState("");
    const [actionType, setActionType] = useState("");
    const [role, setRole] = useState("");
    const [range, setRange] = useState("30d");

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (q) params.q = q;
            if (brand) params.brand = brand;
            if (actionType) params.action_type = actionType;
            if (role) params.role = role;
            if (range !== "all") {
                const days = { "1d": 1, "7d": 7, "30d": 30 }[range] || 30;
                const since = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
                params.start_date = since;
            }
            const { data } = await api.get("/activity-logs", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch {
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [brand, actionType, role, range]);

    const onSearch = (e) => { e.preventDefault(); load(); };

    return (
        <section
            className="mt-6 border border-mist rounded-sm bg-white p-6"
            data-testid="section-activity-history"
        >
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
                data-testid="section-activity-history-toggle"
                className="w-full flex items-center justify-between gap-3 flex-wrap text-left">
                <h2 className="font-serif text-xl md:text-2xl inline-flex items-center gap-2">
                    <ClockCounterClockwise size={18} weight="light" className="text-sage-dark" />
                    Activity History
                </h2>
                <span className="flex items-center gap-2">
                    <span className="eyebrow">{isOwner ? "Owner" : "View only"}</span>
                    <CaretRight size={16} weight="light" className={`shrink-0 text-neutral-400 transition-transform ${open ? "rotate-90" : ""}`} />
                </span>
            </button>
            <div className={open ? "" : "hidden"} data-testid="section-activity-history-body">
            <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                Append-only audit trail. {isOwner
                    ? "You can filter across all brands and roles."
                    : "You see activity on the brands assigned to you; records cannot be edited or deleted."}
            </p>

            <form onSubmit={onSearch} className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search product, SKU, user…"
                    data-testid="activity-search"
                    className="col-span-2 md:col-span-2 border border-mist rounded-sm px-3 py-2 bg-white text-base md:text-sm focus:outline-none focus:border-sage-dark"
                />
                {isOwner && (
                    <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="activity-brand" className="border border-mist rounded-sm px-2 py-2 bg-white text-sm">
                        <option value="">All brands</option>
                        <option value="hoshizora">Hoshizora</option>
                        <option value="heelsandtea">Heels & Tea</option>
                        <option value="system">System</option>
                    </select>
                )}
                <select value={actionType} onChange={(e) => setActionType(e.target.value)} data-testid="activity-action" className="border border-mist rounded-sm px-2 py-2 bg-white text-sm">
                    <option value="">All actions</option>
                    <option value="product">Product</option>
                    <option value="catalog">Catalog</option>
                    <option value="user">Users</option>
                    <option value="auth">Auth</option>
                    <option value="sync">Sync</option>
                </select>
                {isOwner && (
                    <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="activity-role" className="border border-mist rounded-sm px-2 py-2 bg-white text-sm">
                        <option value="">All roles</option>
                        <option value="owner">Owner</option>
                        <option value="admin_input">Admin Input</option>
                        <option value="admin_sales">Admin Sales</option>
                    </select>
                )}
                <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="activity-range" className="border border-mist rounded-sm px-2 py-2 bg-white text-sm">
                    <option value="1d">Today</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="all">All time</option>
                </select>
            </form>

            <div className="mt-5">
                {loading ? (
                    <div className="text-sm text-neutral-400 py-10 text-center">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="text-sm text-neutral-500 py-10 text-center border border-dashed border-mist rounded-sm">
                        No activity in this range.
                    </div>
                ) : (
                    <>
                        <div className="text-xs text-neutral-500 mb-2">
                            {items.length} of {total} record{total === 1 ? "" : "s"}
                        </div>
                        <ul className="divide-y divide-mist border border-mist rounded-sm">
                            {items.map((e) => <ActivityRow key={e.id} e={e} />)}
                        </ul>
                    </>
                )}
            </div>
            </div>
        </section>
    );
}

const BRAND_LABELS = { hoshizora: "Hoshizora", heelsandtea: "Heels & Tea", system: "System" };
const ACTION_LABELS = {
    "product.created": "Product created",
    "product.updated": "Product edited",
    "product.deleted": "Product deleted",
    "product.review_checked": "Product marked Checked",
    "product.review_reset": "Product reset to To Check",
    "product.review_recheck": "Product changed to Recheck",
    "category.created": "Category created",
    "category.updated": "Category edited",
    "category.deleted": "Category deleted",
    "region.created": "Region created",
    "region.updated": "Region edited",
    "region.deleted": "Region deleted",
    "collection.created": "Collection created",
    "collection.updated": "Collection edited",
    "collection.deleted": "Collection deleted",
    "featured.created": "Featured filter added",
    "featured.updated": "Featured filter edited",
    "featured.deleted": "Featured filter removed",
    "catalog.reordered": "Catalog reordered",
    "user.created": "User created",
    "user.updated": "User edited",
    "user.deleted": "User deleted",
    "user.password_reset": "Password reset",
    "sync.jubelio": "Jubelio sync event",
};

function ActivityRow({ e }) {
    const [open, setOpen] = useState(false);
    const label = ACTION_LABELS[e.action] || e.action;
    const when = e.created_at ? new Date(e.created_at) : null;
    const hasChanges = Array.isArray(e.changes) && e.changes.length > 0;
    const brandLabel = BRAND_LABELS[e.brand] || e.brand;

    return (
        <li className="px-3 py-2.5" data-testid={`activity-row-${e.id}`}>
            <button
                type="button"
                onClick={() => hasChanges && setOpen((o) => !o)}
                className="w-full text-left flex items-start gap-3"
            >
                <div className="w-28 shrink-0 text-[11px] text-neutral-500 tabular-nums leading-tight">
                    {when ? when.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    <div className="text-[10px] text-neutral-400">
                        {when ? when.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : ""}
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-widest text-sage-dark">
                        {label}
                    </div>
                    <div className="text-sm text-ink leading-snug mt-0.5">
                        {e.object_label || "—"}
                        {e.sku && <span className="ml-2 font-mono text-xs text-neutral-500">· SKU {e.sku}</span>}
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                        {e.user_name || e.user_email || "System"}
                        {e.user_role && <span className="ml-1">· {e.user_role.replace("_", " ")}</span>}
                        <span className="ml-1">· {brandLabel}</span>
                    </div>
                </div>
                {hasChanges && (
                    <div className="text-[10px] text-neutral-400 uppercase tracking-widest shrink-0">
                        {open ? "Hide" : `${e.changes.length} change${e.changes.length > 1 ? "s" : ""}`}
                    </div>
                )}
            </button>
            {open && hasChanges && (
                <ul className="mt-2 ml-32 space-y-1 text-xs" data-testid={`activity-changes-${e.id}`}>
                    {e.changes.map((c, i) => (
                        <li key={i} className="flex items-baseline gap-2">
                            <span className="eyebrow text-[9px] shrink-0 w-28">{c.label || c.field}</span>
                            <span className="text-neutral-500 line-through break-all">{c.old}</span>
                            <span className="text-neutral-400">→</span>
                            <span className="text-ink break-all">{c.new}</span>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
}

/**
 * Minimal collapsible section header for Settings. Owns no state; children
 * stay mounted outside this component so form inputs are never reset on
 * collapse. Clicking anywhere on the header toggles the section.
 */
function AccordionHeader({ title, open, onToggle, testId }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            data-testid={testId}
            className="w-full flex items-center justify-between gap-3 text-left"
        >
            <h2 className="font-serif text-xl md:text-2xl">{title}</h2>
            <CaretRight
                size={16}
                weight="light"
                className={`shrink-0 text-neutral-400 transition-transform ${open ? "rotate-90" : ""}`}
            />
        </button>
    );
}
